import React, { useCallback, useContext, useMemo } from 'react';
import { useRecoilState } from 'recoil';
import { isEqual } from 'lodash';
import styled from 'styled-components';
import { SelectHeader } from '@ubeya/shared-web/components';
import { ReactComponent as BaseArrowIcon } from '@ubeya/shared-web/assets/arrow-right-small.svg';
import * as sharedAtoms from '@ubeya/shared/atoms/shared';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { isMobileOnly } from 'react-device-detect';
import useGroupedBranchesOptions from '../../hooks/useGroupedBranchesOptions';
import { isBranchGroupHeaderOption } from '../../utils/branches';

export const ArrowIcon = styled(BaseArrowIcon).attrs(() => ({ className: 'branch-picker-arrow-icon' }))`
  transform: rotate(90deg);
`;

const BranchPicker = ({ isOnly, onChangeBranch, style, usePortal }) => {
  const { t } = useContext(ConfigContext);
  const {
    branchesOptions: defaultBranchesOptions,
    groupedBranchesOptions,
    getOptionSpecificStyle,
    filterFunc
  } = useGroupedBranchesOptions();

  const [selectedBranches, setSelectedBranches] = useRecoilState(sharedAtoms.selectedBranches);
  const [selectedBranch, setSelectedBranch] = useRecoilState(sharedAtoms.selectedBranch);

  const allValue = useMemo(() => defaultBranchesOptions.map(({ value }) => value), [defaultBranchesOptions]);

  const branchesOptions = useMemo(() => {
    if (isOnly) {
      return groupedBranchesOptions;
    }

    return [
      { label: t('allBranches'), value: allValue },
      ...groupedBranchesOptions.map((option) =>
        isBranchGroupHeaderOption(option) ? option : { ...option, value: [option.value] }
      )
    ];
  }, [allValue, groupedBranchesOptions, isOnly, t]);

  const value = useMemo(() => (isOnly ? selectedBranch : selectedBranches), [isOnly, selectedBranch, selectedBranches]);

  const onChange = useCallback(
    (changeValue) => {
      if (isOnly) {
        setSelectedBranch(changeValue);
      } else {
        setSelectedBranches(changeValue);
      }
      onChangeBranch?.(changeValue);
    },
    [isOnly, onChangeBranch, setSelectedBranch, setSelectedBranches]
  );

  if (defaultBranchesOptions.length < 2) {
    return null;
  }

  return (
    <SelectHeader
      options={branchesOptions}
      value={value}
      onChange={onChange}
      icon={ArrowIcon}
      style={style}
      valuesComparator={isEqual}
      usePortal={usePortal}
      maxHeight={isMobileOnly ? 400 : null}
      getOptionSpecificStyle={getOptionSpecificStyle}
      filterFunc={filterFunc}
    />
  );
};

export default BranchPicker;
